import { forwardRef, useEffect, useRef } from 'react';

import { cva } from 'cva';
import {
  Table as RACTable,
  type TableProps as RACTableProps,
} from 'react-aria-components/Table';

import { useLatest } from '../../hooks/use_latest';

import styles from './table_view.module.css';

export const tableVariants = cva({
  base: styles.table,
  variants: {
    size: {
      compact: styles.size_compact,
      default: undefined,
      spacious: styles.size_spacious,
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export type TableViewProps = RACTableProps & {
  className?: string;
  /** Table density variant */
  size?: 'compact' | 'default' | 'spacious';
  /** Enable column resizing by dragging column borders */
  allowsResizing?: boolean;
  /** Called when the scroll sentinel enters the scroll container. Pass undefined to disable. */
  onLoadMore?: () => void;
  /**
   * Distance in pixels from the bottom of the scroll container at which
   * `onLoadMore` is triggered. Larger values prefetch the next page earlier.
   * @default 500
   */
  loadMoreThreshold?: number;
};

/**
 * TableView - A data table component with sortable columns, selection, and resizable columns.
 *
 * For resizable columns, wrap the TableView in a ResizableTableContainer:
 *
 * ```tsx
 * import { ResizableTableContainer } from 'react-aria-components';
 *
 * <ResizableTableContainer>
 *   <TableView
 *     aria-label="Files table"
 *     selectionMode="multiple"
 *     allowsResizing={true}
 *   >
 *     <TableHeader>
 *       <Column isRowHeader allowsResizing>Name</Column>
 *       <Column allowsResizing>Type</Column>
 *       <Column>Size</Column>
 *     </TableHeader>
 *     <TableBody>
 *       <Row>
 *         <Cell>Document.pdf</Cell>
 *         <Cell>PDF</Cell>
 *         <Cell>1.2 MB</Cell>
 *       </Row>
 *     </TableBody>
 *   </TableView>
 * </ResizableTableContainer>
 * ```
 *
 * See https://react-spectrum.adobe.com/react-aria/Table.html for more details.
 */
export const TableView = forwardRef<HTMLTableElement, TableViewProps>(
  (
    {
      className,
      size,
      children,
      onLoadMore,
      loadMoreThreshold = 500,
      ...props
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const onLoadMoreRef = useLatest(onLoadMore);

    useEffect(() => {
      if (!onLoadMore || !containerRef.current || !sentinelRef.current) {
        return;
      }
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            onLoadMoreRef.current?.();
          }
        },
        { root: containerRef.current, rootMargin: `${loadMoreThreshold}px` },
      );
      observer.observe(sentinelRef.current);
      return () => observer.disconnect();
    }, [onLoadMore, loadMoreThreshold]);

    return (
      <div ref={containerRef} className={styles.table_container}>
        <RACTable
          ref={ref}
          className={tableVariants({ size, className })}
          {...props}
        >
          {children}
        </RACTable>
        {onLoadMore && (
          <div ref={sentinelRef} className={styles.load_more_sentinel} />
        )}
      </div>
    );
  },
);
